import { Icon as LegacyIcon } from '@ant-design/compatible';
import * as Sentry from '@sentry/browser';
import { Input as AInput, Form } from 'antd';
import Select from 'components/Select/Select';
import SVGIcon from 'components/SVGIcon/SVGIcon';
import TrackingButton from 'components/TrackingComponents/Button';
import TrackingInput from 'components/TrackingComponents/TrackingInput';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { KeepInTouchUserType, SubmitKeepInTouchFormRequest } from 'types/schema';
import media from 'utils/mediaStyle';
import utilsMessages from 'utils/messages';
import messages from '../messages.ts';
import ArrowRight from './arrow-right.svg';

interface IOwnProps {
  submitting: boolean;
  onSubmit: (data: { values: SubmitKeepInTouchFormRequest; cb: (res: any) => void }) => void;
}

type Props = IOwnProps;

const Container = styled.section`
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 8pt;
  flex: 1;
  width: 100%
  border: 1px solid #eaebea;
  padding: 16;
  border-radius: 16px;
  ${media.md`
    padding: 24px 40px 28px;
    max-width: 519px;
  `};
  p {
    line-height: normal;
    margin: 8px 0;
  }
`;

const PageHeader = styled.h3`
  font-size: 20px;
  line-height: 22px;
  ${media.md`
    font-size: 30px;
    font-weight: 600;
    line-height: 40px;
  `};
`;

const PageSubHeader = styled.span`
  font-size: 13px;
  color: #596056;
  line-height: 18px;
  ${media.md`
    font-size: 14px;
  `};
`;

const AntdInput = styled(AInput)<any>`
  border-radius: 8px !important;
`;

const PrefixIcon = styled(LegacyIcon)`
  color: rgba(0, 0, 0, 0.25);
`;

const FormItem = styled(Form.Item)`
  margin-bottom: 12px !important;
`;

const SubmitButton = styled(TrackingButton)`
  margin: 8px auto 0px;
  font-size: 16px;
  color: #ffffff;
  width: fit-content !important;
  background: #43a047;
  display: flex;
  align-items: center;
  gap: 6px;
  &:hover {
    background: #6fc373 !important;
  }
  &:active {
    background: #3c9041 !important;
  }
`;

const HeaderWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin-bottom: 24px;
}
`;

const TextArea = styled(AInput.TextArea)`
  height: 72px;
  & > textarea {
    resize: none;
  }
`;

const ContactUsForm: FC<Props> = ({ onSubmit }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const lang = localStorage.getItem('lang') || 'vi';

  const userType = useMemo(() => {
    return [
      {
        id: 1,
        value: KeepInTouchUserType.Customer,
        label: lang === 'vi' ? 'Đối tác khách hàng' : "I'm a customer",
      },
      {
        id: 2,
        value: KeepInTouchUserType.Supplier,
        label: lang === 'vi' ? 'Đối tác nhà cung cấp' : "I'm a supplier",
      },
      {
        id: 3,
        value: KeepInTouchUserType.Investor,
        label: lang === 'vi' ? 'Nhà đầu tư' : "I'm an investor",
      },
      {
        id: 4,
        value: KeepInTouchUserType.Others,
        label: lang === 'vi' ? 'Khác' : 'Others',
      },
    ];
  }, [lang]);

  return (
    <Container>
      <HeaderWrapper>
        <PageHeader>
          <FormattedMessage {...messages.formHeader} />
        </PageHeader>
        <PageSubHeader>
          <FormattedMessage {...messages.firstFormSubHeader} />
        </PageSubHeader>
        <PageSubHeader>
          <FormattedMessage {...messages.secondFormSubHeader} />
        </PageSubHeader>
      </HeaderWrapper>
      <Form
        form={form}
        onFinish={(values) => {
          setLoading(true);
          onSubmit({
            values: { ...values, lang: lang },
            cb: (response) => {
              setLoading(false);
              if (response.success) {
                form.resetFields();
              }
            },
          });
        }}
        onFinishFailed={(error) => Sentry.captureException(error, { level: Sentry.Severity.Warning })}
        className="login-form"
        size="large"
        layout="vertical"
      >
        <FormattedMessage {...messages.yourName}>
          {(yourName: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: yourName }}>
              {(fieldRequired: string) => (
                <FormItem
                  name="fullName"
                  colon={false}
                  required={true}
                  label={yourName}
                  hasFeedback
                  rules={[
                    {
                      required: true,
                      message: fieldRequired,
                    },
                  ]}
                >
                  <TrackingInput
                    prefix={<PrefixIcon type="user" />}
                    placeholder={yourName}
                    options={{
                      action: 'Enter Username',
                      category: 'Contact Us Form | Input',
                    }}
                  >
                    <AntdInput />
                  </TrackingInput>
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...messages.phone}>
          {(phoneNumber: string) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: phoneNumber }}>
              {(fieldRequired) => (
                <FormattedMessage {...utilsMessages.validatePhone}>
                  {(fieldValidate) => (
                    <FormItem
                      name="phoneNumber"
                      colon={false}
                      required={true}
                      label={phoneNumber}
                      hasFeedback
                      rules={[
                        { required: true, message: fieldRequired },
                        {
                          validator: (_, value) => {
                            if (!value || (value.length >= 10 && value.length <= 11)) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error(fieldValidate as string));
                          },
                        },
                      ]}
                      getValueFromEvent={(event) => {
                        const value = event.target.value.replace(/[^0-9]/g, '');
                        return value;
                      }}
                    >
                      <TrackingInput
                        prefix={<PrefixIcon type="phone" />}
                        placeholder={phoneNumber}
                        autoComplete="phone"
                        options={{
                          action: 'Enter Phone',
                          category: 'Contact Us Form | Input',
                        }}
                      >
                        <AntdInput />
                      </TrackingInput>
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...utilsMessages.fieldEmail}>
          {(fieldEmail) => (
            <FormattedMessage {...utilsMessages.fieldRequired} values={{ field: 'Email' }}>
              {(fieldRequired) => (
                <FormItem
                  name="email"
                  colon={false}
                  required={true}
                  label={`Email`}
                  hasFeedback
                  rules={[
                    {
                      type: 'email',
                      message: fieldEmail,
                    },
                    { required: true, message: fieldRequired },
                  ]}
                  normalize={(value: string) => value && value.replace(/\s/g, '')}
                >
                  <TrackingInput
                    prefix={<PrefixIcon type="mail" />}
                    placeholder="Email"
                    autoComplete="username"
                    options={{
                      action: 'Enter Email',
                      category: 'Contact Us Form | Input',
                    }}
                  >
                    <AntdInput />
                  </TrackingInput>
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...messages.whoYouAre}>
          {(companyName: string) => (
            <FormattedMessage {...messages.validateWhoYouAre} values={{ field: companyName }}>
              {(fieldRequired: string) => (
                <FormattedMessage {...messages.whoAreYou}>
                  {(rolePlaceHolder) => (
                    <FormItem
                      name="userType"
                      colon={false}
                      required={true}
                      label={companyName}
                      hasFeedback
                      rules={[
                        {
                          required: true,
                          message: fieldRequired,
                        },
                      ]}
                    >
                      <Select
                        items={userType.map((item) => ({
                          label: item.label,
                          value: item.value,
                        }))}
                        placeholder={rolePlaceHolder}
                      />
                    </FormItem>
                  )}
                </FormattedMessage>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormattedMessage {...messages.yourMessage}>
          {(yourMessage: string) => (
            <FormattedMessage {...messages.placeAMessage}>
              {(placeAMessage: string) => (
                <FormItem
                  label={yourMessage}
                  name="message"
                  colon={false}
                  required={false}
                  hasFeedback
                  rules={[{ required: false }]}
                >
                  <TextArea size="large" placeholder={placeAMessage} />
                </FormItem>
              )}
            </FormattedMessage>
          )}
        </FormattedMessage>
        <FormItem style={{ marginTop: '12px' }}>
          <SubmitButton
            block
            type="primary"
            htmlType="submit"
            trackingCategory="Contact Us Form | Button"
            trackingAction="Submit Contact Us"
            loading={loading}
          >
            <FormattedMessage {...messages.submitForm} />
            <SVGIcon src={ArrowRight} />
          </SubmitButton>
        </FormItem>
      </Form>
    </Container>
  );
};

export default ContactUsForm;
